import React, { Fragment } from "react";

import img13 from "../../../images/service/E-Commerce Blockchain Development/agriculture.png";
import img14 from "../../../images/service/E-Commerce Blockchain Development/agriculture (2).png";
import img15 from "../../../images/service/E-Commerce Blockchain Development/agriculture (3).png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Farm-to-Consumer Traceability",
    content:
      "Track agricultural products from farm to table with immutable records ensuring food safety and authenticity.",
  },
  {
    icon: sIcon2,
    title: "Smart Agricultural Contracts",
    content:
      "Automate crop insurance, commodity trading, and payment settlements with transparent smart contract logic.",
  },
  {
    icon: sIcon3,
    title: "Supply Chain Transparency",
    content:
      "Provide complete visibility into agricultural supply chains, reducing fraud and improving quality control.",
  },
];

const benefits = [
  {
    title: "Enhanced Traceability",
    description:
      "Track every step of the agricultural supply chain from seed to consumer, ensuring food safety and product authenticity.",
    icon: sIcon1,
  },
  {
    title: "Fair Farmer Compensation",
    description:
      "Direct market access and transparent pricing eliminate middlemen, ensuring farmers receive fair value for their produce.",
    icon: sIcon2,
  },
  {
    title: "Reduced Food Fraud",
    description:
      "Immutable records prevent counterfeit products, mislabeling, and fraudulent claims about organic or sustainable farming.",
    icon: sIcon3,
  },
  {
    title: "Efficient Insurance Claims",
    description:
      "Smart contracts automate crop insurance payouts based on verified weather data and yield information.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What is agriculture blockchain development?",
    a: "Agriculture blockchain development creates transparent, traceable systems for food supply chains, farm management, and agricultural commerce using distributed ledger technology.",
  },
  {
    q: "How does blockchain improve agriculture?",
    a: "Blockchain enhances traceability, reduces fraud, ensures fair pricing for farmers, automates insurance claims, and provides transparent supply chain visibility from farm to consumer.",
  },
  {
    q: "What can be tracked with blockchain in agriculture?",
    a: "Origin and journey of crops, pesticide usage, organic certification, harvest dates, storage conditions, transportation, quality inspections, and ownership transfers throughout the supply chain.",
  },
  {
    q: "Is blockchain secure for agricultural data?",
    a: "Yes—blockchain's decentralized structure and cryptographic security protect agricultural data from tampering while ensuring transparency for authorized stakeholders.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function AgricultureBlockchainDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Agriculture Blockchain Development"
        pagesub="Transform farming and food systems with transparent blockchain solutions"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            Modernize{" "}
            <span className="gradient-text">Agriculture</span>{" "}
            with Blockchain
          </>
        }
        subtitle={`Leverage blockchain technology to create transparent, efficient agricultural ecosystems that 
ensure food safety, fair farmer compensation, and complete supply chain visibility from farm to table.`}
        buttonText="Book Consultation"
        buttonLink="/contact"
        image={img13}
        imageAlt="Agriculture Blockchain Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Blockchain for Agriculture?"
        image={img14}
        paragraphs={[
          "Blockchain transforms agriculture by providing end-to-end traceability, ensuring food safety, and eliminating intermediaries that reduce farmer profits. Every stakeholder gains real-time visibility into the supply chain.",
          "From organic certification to crop insurance automation, blockchain creates trust in agricultural systems while enabling farmers to connect directly with consumers and receive fair compensation for their work.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Core Agriculture Blockchain Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Challenges in Traditional Agriculture"
        image={img15}
        paragraphs={[
          "Traditional agricultural systems suffer from limited supply chain visibility, food fraud, unfair pricing for farmers, lengthy insurance claim processes, and difficulty verifying organic or sustainable practices.",
          "Blockchain addresses these challenges by creating immutable records of agricultural activities, automating processes through smart contracts, and providing transparent, real-time data access to all stakeholders.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Benefits of Blockchain in Agriculture"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our Agriculture Blockchain Solutions"
        subtitle="Build trust and efficiency in food systems with innovative technology"
        features={[
          {
            icon: sIcon1,
            title: "End-to-End Traceability",
            description:
              "Track products from seed planting through harvest, processing, and retail distribution.",
          },
          {
            icon: sIcon2,
            title: "Quality Certification",
            description:
              "Record and verify organic, fair-trade, and sustainability certifications immutably.",
          },
          {
            icon: sIcon3,
            title: "IoT Integration",
            description:
              "Connect sensors for automated recording of temperature, humidity, and storage conditions.",
          },
          {
            icon: sIcon4,
            title: "Direct Marketplace",
            description:
              "Enable farmers to sell directly to consumers or retailers without intermediary fees.",
          },
          {
            icon: sIcon5,
            title: "Automated Insurance",
            description:
              "Smart contracts process crop insurance claims automatically based on verified data.",
          },
          {
            icon: sIcon6,
            title: "Regulatory Compliance",
            description:
              "Maintain audit-ready records for food safety regulations and quality standards.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Expertise in agricultural supply chain and blockchain technology",
          "IoT integration for automated data collection and recording",
          "Proven solutions that improve farmer livelihoods and food safety",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
